import { useCallback, useEffect, useState } from "react";
import { dashboardApi } from "../../api/dashboardApi";
import { frigomatApi, type FrigomatMachine, type FrigomatMachineType, type FrigomatAlarmEntry } from "../../api/frigomatApi";
import { useAuth } from "../../auth/AuthContext";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import "../../styles/frigomat.css";

function formatLastUpdate(s?: string | null): string {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return isNaN(d.getTime()) ? s : d.toLocaleString("it-IT");
  } catch {
    return s;
  }
}

function resolveModelName(
  modelId: number | undefined,
  catalog: FrigomatMachineType[]
): string {
  if (modelId == null) return "—";
  const m = catalog.find((c) => c.id === modelId || c.modelType === modelId);
  return m?.descrizione && m.descrizione !== "-" ? m.descrizione : `Modello ${modelId}`;
}

export function FrigomatDashboardWidgets() {
  const { token } = useAuth() as any;
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<any>(null);
  const [wiManagerMachines, setWiManagerMachines] = useState<FrigomatMachine[]>([]);
  const [machineCatalog, setMachineCatalog] = useState<FrigomatMachineType[]>([]);
  const [selectedSerial, setSelectedSerial] = useState<string | null>(null);
  const [alarmsHistory, setAlarmsHistory] = useState<FrigomatAlarmEntry[]>([]);
  const [alarmsLoading, setAlarmsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wiManagerError, setWiManagerError] = useState<string | null>(null);
  const [realtimeInfo, setRealtimeInfo] = useState<{
    siteId: string | null;
    appToken: string | null | Record<string, unknown>;
    realtimeUrl: string;
  } | null>(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    dashboardApi
      .getVendorOverview(token, "FRIGOMAT")
      .then((res) => setOverview(res))
      .catch((e) =>
        setError(e?.message ?? "Errore caricamento dashboard Frigomat")
      )
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!token) return;
    setWiManagerError(null);
    Promise.all([
      frigomatApi.getMachines(token),
      frigomatApi.getMachineListCatalog(token),
    ])
      .then(([machines, catalog]) => {
        setWiManagerMachines(Array.isArray(machines) ? machines : []);
        setMachineCatalog(Array.isArray(catalog) ? catalog : []);
        if (Array.isArray(machines) && machines.length > 0 && !selectedSerial) {
          const first = machines[0] as FrigomatMachine;
          const serial = String(first.maC_Numero_Serie_MS ?? first.maC_Matricola ?? "").trim();
          if (serial) setSelectedSerial(serial);
        }
      })
      .catch((e) => setWiManagerError(e?.message ?? "Errore WiManager"));
  // selectedSerial intentionally omitted: we only set it when empty, avoid refetch on selection change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchAlarms = useCallback(() => {
    if (!token || !selectedSerial) {
      setAlarmsHistory([]);
      return;
    }
    setAlarmsLoading(true);
    frigomatApi
      .getAlarmsHistory(token, selectedSerial)
      .then((data) => setAlarmsHistory(Array.isArray(data) ? data : []))
      .catch(() => setAlarmsHistory([]))
      .finally(() => setAlarmsLoading(false));
  }, [token, selectedSerial]);

  useEffect(() => {
    fetchAlarms();
  }, [fetchAlarms]);

  useEffect(() => {
    if (!token) return;
    frigomatApi
      .getRealtimeInfo(token)
      .then((info) => setRealtimeInfo(info))
      .catch(() => setRealtimeInfo(null));
  }, [token]);

  const energyData = Array.isArray(overview?.energyLast7d)
    ? overview.energyLast7d
    : [];
  const alarmsData =
    Array.isArray(overview?.alarmsByMachine) && overview.alarmsByMachine.length > 0
      ? overview.alarmsByMachine
      : Array.isArray(overview?.alarmsByCode)
        ? overview.alarmsByCode
        : [];

  const totalEnergyToday =
    Array.isArray(overview?.energyLast7d) && overview.energyLast7d.length > 0
      ? overview.energyLast7d[overview.energyLast7d.length - 1]?.kwh ?? 0
      : 0;

  if (loading) {
    return (
      <div className="frigomat-card frigomat-card-glow">
        <div className="card-body">
          <div className="text-muted d-flex align-items-center gap-2">
            <div className="spinner-border spinner-border-sm text-primary" />
            Caricamento dashboard Frigomat…
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-warning frigomat-alert">
        <strong>Dashboard Frigomat</strong>: {error}
      </div>
    );
  }

  return (
    <div className="frigomat-dashboard">
      {/* Hero Section */}
      <div className="frigomat-hero">
        <div className="frigomat-hero-content">
          <div className="frigomat-hero-brand">
            <div className="frigomat-hero-icon">
              <i className="bi bi-snow2" />
            </div>
            <div>
              <h2 className="frigomat-hero-title">Frigomat WiManager</h2>
              <p className="frigomat-hero-sub">
                Monitoraggio macchine refrigerazione · Dati in tempo reale
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="row g-3 g-lg-4 mb-4">
        <div className="col-6 col-lg-3">
          <div className="frigomat-kpi-card frigomat-kpi-temp">
            <div className="frigomat-kpi-icon">
              <i className="bi bi-thermometer-half" />
            </div>
            <div className="frigomat-kpi-label">Temperatura media</div>
            <div className="frigomat-kpi-value">
              {overview?.avgTemperature != null
                ? `${overview.avgTemperature.toFixed(1)}°C`
                : "—"}
            </div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="frigomat-kpi-card frigomat-kpi-energy">
            <div className="frigomat-kpi-icon">
              <i className="bi bi-lightning-charge-fill" />
            </div>
            <div className="frigomat-kpi-label">Consumo oggi</div>
            <div className="frigomat-kpi-value">
              {totalEnergyToday > 0
                ? `${totalEnergyToday.toFixed(1)} kWh`
                : "—"}
            </div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="frigomat-kpi-card frigomat-kpi-alarms">
            <div className="frigomat-kpi-icon">
              <i className="bi bi-exclamation-triangle-fill" />
            </div>
            <div className="frigomat-kpi-label">Allarmi attivi</div>
            <div className="frigomat-kpi-value">
              {overview?.alarmCount ?? overview?.activeAlarms ?? 0}
            </div>
          </div>
        </div>
        <div className="col-6 col-lg-3">
          <div className="frigomat-kpi-card frigomat-kpi-online">
            <div className="frigomat-kpi-icon">
              <i className="bi bi-check-circle-fill" />
            </div>
            <div className="frigomat-kpi-label">Macchine online</div>
            <div className="frigomat-kpi-value">
              {overview?.onlineCount ?? overview?.onlineMachines ?? 0} /{" "}
              {overview?.machinesCount ?? overview?.totalMachines ?? 0}
            </div>
          </div>
        </div>
      </div>

      {/* Vista real-time cloud */}
      {realtimeInfo?.realtimeUrl && (
        <div className="frigomat-section mb-4">
          <div className="frigomat-section-header">
            <i className="bi bi-broadcast" />
            <span>Stato macchine in tempo reale (cloud)</span>
          </div>
          <div className="frigomat-card p-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div>
                <p className="mb-1 small text-muted">
                  Verifica lo stato delle macchine direttamente dal cloud Frigomat WiManager.
                </p>
                {realtimeInfo.siteId && (
                  <span className="badge bg-secondary me-1">siteId: {realtimeInfo.siteId}</span>
                )}
              </div>
              <a
                href={realtimeInfo.realtimeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn frigomat-btn-primary"
              >
                <i className="bi bi-box-arrow-up-right me-1" />
                Apri vista real-time
              </a>
            </div>
          </div>
        </div>
      )}

      {/* WiManager Macchine */}
      <div className="frigomat-section mb-4">
        <div className="frigomat-section-header">
          <i className="bi bi-gear-wide-connected" />
          <span>Macchine WiManager</span>
        </div>
        {wiManagerError ? (
          <div className="frigomat-card p-3">
            <p className="text-warning mb-0 small">
              <i className="bi bi-exclamation-circle me-2" />
              {wiManagerError}
            </p>
          </div>
        ) : wiManagerMachines.length === 0 ? (
          <div className="frigomat-card p-4 text-center text-muted">
            Nessuna macchina collegata a WiManager
          </div>
        ) : (
          <div className="row g-3">
            {wiManagerMachines.map((m) => {
              const serial = String(m.maC_Numero_Serie_MS ?? m.maC_Matricola ?? "").trim();
              const isSelected = selectedSerial === serial;
              const modelName =
                resolveModelName(m.maC_ModelloSx, machineCatalog) !== "—"
                  ? resolveModelName(m.maC_ModelloSx, machineCatalog)
                  : resolveModelName(m.maC_Macchina, machineCatalog);
              return (
                <div key={serial || m.maC_Id} className="col-12 col-md-6 col-lg-4">
                  <div
                    className={`frigomat-machine-card ${isSelected ? "frigomat-machine-card-active" : ""}`}
                    onClick={() => setSelectedSerial(serial)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      (e.key === "Enter" || e.key === " ") && setSelectedSerial(serial)
                    }
                  >
                    <div className="frigomat-machine-card-header">
                      <span className="frigomat-machine-serial">
                        S/N {m.maC_Matricola ?? serial}
                      </span>
                      <span className="frigomat-machine-model">{modelName}</span>
                    </div>
                    <div className="frigomat-machine-card-body">
                      <div className="frigomat-machine-row">
                        <i className="bi bi-hash" />
                        <span>{m.maC_Numero_Serie_MS ?? "—"}</span>
                      </div>
                      <div className="frigomat-machine-row">
                        <i className="bi bi-clock" />
                        <span>{formatLastUpdate(m.maC_LastUpdate)}</span>
                      </div>
                      {m.maC_VersSwInt && (
                        <div className="frigomat-machine-row">
                          <i className="bi bi-code-slash" />
                          <span>v{m.maC_VersSwInt}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Storico allarmi macchina selezionata */}
      {selectedSerial && (
        <div className="frigomat-section mb-4">
          <div className="frigomat-section-header">
            <i className="bi bi-bell" />
            <span>Storico allarmi · S/N {selectedSerial}</span>
            <button
              type="button"
              className="btn btn-sm btn-outline-light ms-auto"
              onClick={fetchAlarms}
              disabled={alarmsLoading}
            >
              <i className={`bi ${alarmsLoading ? "bi-arrow-repeat spin" : "bi-arrow-clockwise"}`} />
              Aggiorna
            </button>
          </div>
          <div className="frigomat-card">
            <div className="frigomat-table-responsive">
              {alarmsLoading && alarmsHistory.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <div className="spinner-border spinner-border-sm me-2" />
                  Caricamento allarmi…
                </div>
              ) : alarmsHistory.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-check2-circle fs-2 d-block mb-2" />
                  Nessun allarme in storico
                </div>
              ) : (
                <table className="table frigomat-table mb-0">
                  <thead>
                    <tr>
                      <th>Data/Ora</th>
                      <th>Codice</th>
                      <th>ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alarmsHistory.slice(0, 50).map((a, idx) => (
                      <tr key={a.saL_Id ?? idx}>
                        <td>{formatLastUpdate(a.saL_DateTime)}</td>
                        <td>
                          <span className="frigomat-badge frigomat-badge-danger">
                            {a.saL_Allarme ?? "—"}
                          </span>
                        </td>
                        <td className="text-muted small">{a.saL_Id ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="row g-4 mb-4">
        <div className="col-12 col-lg-7">
          <div className="frigomat-card frigomat-card-chart">
            <div className="frigomat-card-header">
              <i className="bi bi-bar-chart-fill" />
              Consumi ultimi 7 giorni
            </div>
            <div className="card-body frigomat-chart-container">
              {energyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={energyData}>
                    <XAxis dataKey="day" />
                    <YAxis />
                    <Tooltip
                      formatter={(value: number) => [
                        `${value.toFixed(2)} kWh`,
                        "Consumo",
                      ]}
                    />
                    <Legend />
                    <Bar
                      dataKey="kwh"
                      fill="url(#frigomatBarGradient)"
                      radius={[8, 8, 0, 0]}
                    />
                    <defs>
                      <linearGradient
                        id="frigomatBarGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#38bdf8" />
                        <stop offset="100%" stopColor="#0ea5e9" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-muted text-center py-5">
                  <i className="bi bi-graph-up fs-2 d-block mb-2" />
                  Nessun dato consumo disponibile
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-5">
          <div className="frigomat-card frigomat-card-chart">
            <div className="frigomat-card-header">
              <i className="bi bi-pie-chart-fill" />
              Allarmi per macchina
            </div>
            <div className="card-body">
              {alarmsData.length > 0 ? (
                <ul className="list-unstyled mb-0 frigomat-alarms-list">
                  {alarmsData.slice(0, 8).map((item: any, idx) => (
                    <li key={idx} className="frigomat-alarms-item">
                      <span>{item.name || item.label || `Macchina ${idx + 1}`}</span>
                      <span className="frigomat-badge frigomat-badge-danger">
                        {item.value ?? item.count ?? 0}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-muted text-center py-5">
                  <i className="bi bi-emoji-smile fs-2 d-block mb-2" />
                  Nessun allarme attivo
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
